import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  BarChart3,
  Users,
  TrendingDown,
  TrendingUp,
  Briefcase,
  Calendar,
  Star,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const TABS = [
  { id: "overview",    icon: BarChart3 },
  { id: "workforce",   icon: Users },
  { id: "absenteeism", icon: Calendar },
  { id: "payroll",     icon: DollarSign },
  { id: "performance", icon: Star },
];

const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } } },
};

function KPICard({ label, value, sub, icon: Icon, color = "blue", trend, loading }) {
  const colors = {
    blue:   { bg: "bg-blue-50",   text: "text-blue-600",   icon: "text-blue-500" },
    green:  { bg: "bg-green-50",  text: "text-green-600",  icon: "text-green-500" },
    red:    { bg: "bg-red-50",    text: "text-red-600",    icon: "text-red-500" },
    orange: { bg: "bg-orange-50", text: "text-orange-600", icon: "text-orange-500" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", icon: "text-purple-500" },
    teal:   { bg: "bg-teal-50",   text: "text-teal-600",   icon: "text-teal-500" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${c.bg}`}>
          <Icon className={`h-5 w-5 ${c.icon}`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-gray-400"}`}>
            {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : trend < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      {loading ? (
        <div className="h-7 w-20 bg-gray-100 rounded animate-pulse mb-1" />
      ) : (
        <div className={`text-2xl font-bold ${c.text}`}>{value ?? "—"}</div>
      )}
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [hcTrend, setHcTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("v1/executive-dashboard/overview"),
      apiClient.get("v1/executive-dashboard/headcount/trend?months=12"),
    ]).then(([ov, trend]) => {
      setData(ov.data);
      setHcTrend(trend.data);
    }).finally(() => setLoading(false));
  }, []);

  const kpis = [
    { label: t("executiveDashboardHub.overview.kpis.totalHeadcount"), value: data?.headcount, icon: Users, color: "blue" },
    { label: t("executiveDashboardHub.overview.kpis.newHiresMonth"), value: data?.new_hires_this_month, icon: TrendingUp, color: "green" },
    { label: t("executiveDashboardHub.overview.kpis.annualTurnover"), value: data?.turnover_rate != null ? `${data.turnover_rate}%` : null, icon: TrendingDown, color: "orange" },
    { label: t("executiveDashboardHub.overview.kpis.absenteeismRate"), value: data?.absenteeism_rate != null ? `${data.absenteeism_rate}%` : null, icon: Calendar, color: "red" },
    { label: t("executiveDashboardHub.overview.kpis.openPositions"), value: data?.open_positions, icon: Briefcase, color: "purple" },
    { label: t("executiveDashboardHub.overview.kpis.fillRate"), value: data?.fill_rate != null ? `${data.fill_rate}%` : null, icon: BarChart3, color: "teal" },
    { label: t("executiveDashboardHub.overview.kpis.avgPerformance"), value: data?.avg_performance_score ? `${data.avg_performance_score}/5` : t("executiveDashboardHub.common.notAvailable"), icon: Star, color: "blue" },
    { label: t("executiveDashboardHub.overview.kpis.absenceDaysMonth"), value: data?.absent_days_this_month, icon: Calendar, color: "red" },
  ];

  const trendChart = hcTrend ? {
    labels: hcTrend.map(d => d.month),
    datasets: [{
      label: t("executiveDashboardHub.overview.headcountDataset"),
      data: hcTrend.map(d => d.headcount),
      borderColor: "#3b82f6",
      backgroundColor: "rgba(59,130,246,0.08)",
      fill: true,
      tension: 0.4,
      pointRadius: 3,
    }],
  } : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <KPICard key={k.label} {...k} loading={loading} />
        ))}
      </div>

      {trendChart && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">{t("executiveDashboardHub.overview.headcountTrendTitle")}</h3>
          <div className="h-56">
            <Line data={trendChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Workforce Tab ─────────────────────────────────────────────────────────────

function WorkforceTab() {
  const { t } = useTranslation();
  const [hc, setHc] = useState(null);
  const [turnover, setTurnover] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("v1/executive-dashboard/headcount"),
      apiClient.get("v1/executive-dashboard/turnover"),
    ]).then(([h, t]) => {
      setHc(h.data);
      setTurnover(t.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("executiveDashboardHub.common.loading")}</div>;

  const PALETTE = ["#3b82f6","#8b5cf6","#10b981","#f59e0b","#ef4444","#06b6d4","#f97316","#84cc16","#ec4899","#6366f1"];

  const deptChart = hc?.by_department ? {
    labels: hc.by_department.map(d => d.department),
    datasets: [{
      data: hc.by_department.map(d => d.count),
      backgroundColor: PALETTE,
    }],
  } : null;

  const contractChart = hc?.by_contract ? {
    labels: hc.by_contract.map(d => d.type),
    datasets: [{
      data: hc.by_contract.map(d => d.count),
      backgroundColor: PALETTE,
    }],
  } : null;

  const genderChart = hc?.by_gender ? {
    labels: hc.by_gender.map(g => g.gender || t("executiveDashboardHub.common.notSpecified")),
    datasets: [{
      data: hc.by_gender.map(g => g.count),
      backgroundColor: ["#3b82f6", "#ec4899", "#9ca3af"],
    }],
  } : null;

  const turnoverChart = turnover?.monthly_trend ? {
    labels: turnover.monthly_trend.map(d => d.month),
    datasets: [{
      label: t("executiveDashboardHub.workforce.departuresDataset"),
      data: turnover.monthly_trend.map(d => d.departed),
      backgroundColor: "rgba(239,68,68,0.7)",
      borderRadius: 4,
    }],
  } : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label={t("executiveDashboardHub.workforce.kpis.totalHeadcount")} value={hc?.total} icon={Users} color="blue" />
        <KPICard label={t("executiveDashboardHub.workforce.kpis.newHiresYear")} value={hc?.new_this_year} icon={TrendingUp} color="green" />
        <KPICard label={t("executiveDashboardHub.workforce.kpis.turnoverRate")} value={turnover ? `${turnover.annual_turnover_rate}%` : "—"} icon={TrendingDown} color="orange" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {deptChart && (
          <div className="bg-white rounded-xl border p-5 md:col-span-1">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.workforce.byDepartment")}</h3>
            <div className="h-52">
              <Doughnut data={deptChart} options={chartDefaults} />
            </div>
          </div>
        )}
        {contractChart && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.workforce.byContractType")}</h3>
            <div className="h-52">
              <Doughnut data={contractChart} options={chartDefaults} />
            </div>
          </div>
        )}
        {genderChart && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.workforce.byGender")}</h3>
            <div className="h-52">
              <Doughnut data={genderChart} options={chartDefaults} />
            </div>
          </div>
        )}
      </div>

      {turnoverChart && (
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">{t("executiveDashboardHub.workforce.monthlyDeparturesTitle")}</h3>
            <Badge variant="outline" className="text-xs">
              {t("executiveDashboardHub.workforce.departedThisYear", { count: turnover?.departed_this_year })}
            </Badge>
          </div>
          <div className="h-48">
            <Bar data={turnoverChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
          </div>
        </div>
      )}

      {hc?.by_department && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">{t("executiveDashboardHub.workforce.headcountByDepartment")}</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2.5 px-5 font-medium text-gray-600">{t("executiveDashboardHub.workforce.columns.department")}</th>
                <th className="text-right py-2.5 px-5 font-medium text-gray-600">{t("executiveDashboardHub.workforce.columns.headcount")}</th>
                <th className="text-right py-2.5 px-5 font-medium text-gray-600">{t("executiveDashboardHub.workforce.columns.share")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {hc.by_department.map((d) => (
                <tr key={d.department} className="hover:bg-gray-50">
                  <td className="py-2.5 px-5 text-gray-800">{d.department}</td>
                  <td className="py-2.5 px-5 text-right font-semibold text-blue-600">{d.count}</td>
                  <td className="py-2.5 px-5 text-right text-gray-500">
                    {hc.total ? `${((d.count / hc.total) * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Absenteeism Tab ───────────────────────────────────────────────────────────

function AbsenteeismTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/executive-dashboard/absenteeism").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("executiveDashboardHub.common.loading")}</div>;

  const monthlyChart = data?.monthly_trend ? {
    labels: data.monthly_trend.map(d => d.month),
    datasets: [{
      label: t("executiveDashboardHub.absenteeism.absenceDaysDataset"),
      data: data.monthly_trend.map(d => d.days),
      borderColor: "#ef4444",
      backgroundColor: "rgba(239,68,68,0.08)",
      fill: true,
      tension: 0.4,
      pointRadius: 3,
    }],
  } : null;

  const byTypeChart = data?.by_type?.length ? {
    labels: data.by_type.map(d => d.type),
    datasets: [{
      data: data.by_type.map(d => d.days),
      backgroundColor: ["#3b82f6","#ef4444","#f59e0b","#10b981","#8b5cf6","#f97316","#06b6d4","#ec4899"],
    }],
  } : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label={t("executiveDashboardHub.absenteeism.kpis.rate")} value={`${data?.absenteeism_rate_pct ?? 0}%`} icon={TrendingDown} color="red" />
        <KPICard label={t("executiveDashboardHub.absenteeism.kpis.daysThisMonth")} value={data?.absent_days_this_month} icon={Calendar} color="orange" />
        <KPICard label={t("executiveDashboardHub.absenteeism.kpis.daysThisYear")} value={data?.absent_days_this_year} icon={Calendar} color="purple" />
      </div>

      {monthlyChart && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">{t("executiveDashboardHub.absenteeism.monthlyTitle")}</h3>
          <div className="h-52">
            <Line data={monthlyChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {byTypeChart && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.absenteeism.byTypeYear")}</h3>
            <div className="h-52">
              <Doughnut data={byTypeChart} options={chartDefaults} />
            </div>
          </div>
        )}

        {data?.by_department?.length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50">
              <span className="font-semibold text-gray-800 text-sm">{t("executiveDashboardHub.absenteeism.byDepartmentYear")}</span>
            </div>
            <div className="divide-y">
              {data.by_department.map((d) => {
                const max = data.by_department[0].days;
                return (
                  <div key={d.department} className="px-5 py-2.5">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{d.department}</span>
                      <span className="font-semibold text-red-500">{d.days.toFixed(0)}j</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div className="h-1.5 bg-red-400 rounded-full" style={{ width: `${(d.days / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Payroll Tab ───────────────────────────────────────────────────────────────

function PayrollTab() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/executive-dashboard/payroll").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("executiveDashboardHub.common.loading")}</div>;

  if (!data?.available) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2">
        <DollarSign className="h-10 w-10 text-gray-300" />
        <p>{t("executiveDashboardHub.payroll.noValidatedPayroll")}</p>
      </div>
    );
  }

  const trendChart = data.trend?.length ? {
    labels: data.trend.map(d => d.period),
    datasets: [{
      label: t("executiveDashboardHub.payroll.grossPayrollDataset"),
      data: data.trend.map(d => d.total_gross),
      borderColor: "#10b981",
      backgroundColor: "rgba(16,185,129,0.08)",
      fill: true,
      tension: 0.4,
      pointRadius: 3,
    }],
  } : null;

  const deptChart = data.by_department?.length ? {
    labels: data.by_department.map(d => d.department),
    datasets: [{
      label: t("executiveDashboardHub.payroll.grossDataset"),
      data: data.by_department.map(d => d.gross),
      backgroundColor: "rgba(59,130,246,0.7)",
      borderRadius: 4,
    }],
  } : null;

  const fmt = (v) => `${v?.toLocaleString(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${data.currency || "TND"}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-700">{t("executiveDashboardHub.payroll.lastPeriod")}</h3>
        <Badge variant="outline">{data.period}</Badge>
        <Badge variant="secondary">{t("executiveDashboardHub.payroll.employeeCount", { count: data.employee_count })}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label={t("executiveDashboardHub.payroll.kpis.grossPayroll")} value={fmt(data.total_gross)} icon={DollarSign} color="green" />
        <KPICard label={t("executiveDashboardHub.payroll.kpis.netPayroll")} value={fmt(data.total_net)} icon={DollarSign} color="teal" />
        <KPICard label={t("executiveDashboardHub.payroll.kpis.avgGross")} value={fmt(data.avg_gross)} icon={Users} color="blue" />
        <KPICard label={t("executiveDashboardHub.payroll.kpis.avgNet")} value={fmt(data.avg_net)} icon={Users} color="purple" />
      </div>

      {trendChart && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">{t("executiveDashboardHub.payroll.trendTitle")}</h3>
          <div className="h-48">
            <Line data={trendChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
          </div>
        </div>
      )}

      {deptChart && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">{t("executiveDashboardHub.payroll.byDepartmentTitle")}</h3>
          <div className="h-52">
            <Bar data={deptChart} options={{ ...chartDefaults, indexAxis: "y", plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Performance Tab ───────────────────────────────────────────────────────────

function PerformanceTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [positions, setPositions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("v1/executive-dashboard/performance"),
      apiClient.get("v1/executive-dashboard/positions"),
    ]).then(([p, pos]) => {
      setData(p.data);
      setPositions(pos.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("executiveDashboardHub.common.loading")}</div>;

  const distChart = data?.score_distribution?.length ? {
    labels: data.score_distribution.map(d => d.bucket),
    datasets: [{
      label: t("executiveDashboardHub.performance.reviewsDataset"),
      data: data.score_distribution.map(d => d.count),
      backgroundColor: ["#ef4444","#f59e0b","#10b981","#3b82f6"],
      borderRadius: 4,
    }],
  } : null;

  const posChart = positions ? {
    labels: [
      t("executiveDashboardHub.performance.positions.open"),
      t("executiveDashboardHub.performance.positions.partiallyFilled"),
      t("executiveDashboardHub.performance.positions.filled"),
    ],
    datasets: [{
      data: [positions.open, positions.partially_filled, positions.filled],
      backgroundColor: ["#ef4444","#f59e0b","#10b981"],
    }],
  } : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label={t("executiveDashboardHub.performance.kpis.completedReviewsYear")} value={data?.completed_reviews} icon={Star} color="blue" />
        <KPICard label={t("executiveDashboardHub.performance.kpis.avgScore")} value={data?.avg_score ? `${data.avg_score}/5` : t("executiveDashboardHub.common.notAvailable")} icon={Star} color="green" />
        <KPICard label={t("executiveDashboardHub.performance.kpis.positionFillRate")} value={positions ? `${positions.fill_rate_pct}%` : "—"} icon={Briefcase} color="teal" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {distChart && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.performance.scoreDistributionTitle")}</h3>
            <div className="h-52">
              <Bar data={distChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
            </div>
          </div>
        )}

        {posChart && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.performance.positionsStatusTitle")}</h3>
            <div className="h-52">
              <Doughnut data={posChart} options={chartDefaults} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs text-gray-500">
              <div><div className="font-bold text-red-500 text-lg">{positions.open}</div>{t("executiveDashboardHub.performance.positions.open")}</div>
              <div><div className="font-bold text-amber-500 text-lg">{positions.partially_filled}</div>{t("executiveDashboardHub.performance.positions.partials")}</div>
              <div><div className="font-bold text-green-500 text-lg">{positions.filled}</div>{t("executiveDashboardHub.performance.positions.filled")}</div>
            </div>
          </div>
        )}
      </div>

      {positions && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t("executiveDashboardHub.performance.approvedVsActualTitle")}</h3>
          <div className="flex items-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{positions.headcount_approved}</div>
              <div className="text-xs text-gray-500 mt-1">{t("executiveDashboardHub.performance.approvedPositions")}</div>
            </div>
            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${positions.headcount_approved ? Math.min(100, (positions.headcount_filled / positions.headcount_approved) * 100) : 0}%` }}
              />
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{positions.headcount_filled}</div>
              <div className="text-xs text-gray-500 mt-1">{t("executiveDashboardHub.performance.filledPositions")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hub ────────────────────────────────────────────────────────────────────────

export default function ExecutiveDashboardHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const activeTab = searchParams.get("tab") || "overview";

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "overview":    return <OverviewTab key={refreshKey} />;
      case "workforce":   return <WorkforceTab key={refreshKey} />;
      case "absenteeism": return <AbsenteeismTab key={refreshKey} />;
      case "payroll":     return <PayrollTab key={refreshKey} />;
      case "performance": return <PerformanceTab key={refreshKey} />;
      default:            return <OverviewTab key={refreshKey} />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-indigo-50">
            <BarChart3 className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("executiveDashboardHub.title")}</h1>
            <p className="text-sm text-gray-500">{t("executiveDashboardHub.subtitle")}</p>
          </div>
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          {t("executiveDashboardHub.common.refresh")}
        </button>
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(`executiveDashboardHub.tabs.${tab.id}`)}
            </button>
          );
        })}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
